import { Box, IconButton, keyframes, Portal } from '@chakra-ui/react';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { SnoozeIcon } from '../../icons/snooze-icon';

interface Props {
  anchorRef: React.RefObject<HTMLElement>;
  message: React.ReactNode;
  onDismiss: () => void;
  /**
   * Direction of the tooltip relative to the anchor element. "none" disables the arrow entirely
   * while otherwise behaving like the "top" placement.
   */
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'none';
  /** Where horizontally to place the arrow relative to the tooltip (only applies to top/bottom placement) */
  arrowPosition?: 'left' | 'center' | 'right';
  /** Delay in ms before the wiggle animation starts */
  animationDelay: number;
  /** Whether the user can dismiss the tip via the close (x) button */
  showDismiss?: boolean;
}

const wiggle = keyframes`
  0% { transform: translateY(0); }
  50% { transform: translateY(-2px); }
  100% { transform: translateY(0); }
`;

export function ContextualTip({
  anchorRef,
  message,
  onDismiss,
  placement = 'top',
  arrowPosition = 'center',
  animationDelay,
  showDismiss = true,
}: Props) {
  const [style, setStyle] = useState<React.CSSProperties>();
  const [animate, setAnimate] = useState(false);
  const timeoutRef = useRef<number>();

  const updatePositionStable = useCallback(() => {
    const node = anchorRef.current;
    if (!node) {
      return;
    }

    // If the anchor element has zero size (e.g. it only contains absolutely positioned children),
    // try to use its first child element for positioning.
    let rect = node.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) {
      const firstChild = node.firstElementChild as HTMLElement | null;
      if (firstChild) {
        rect = firstChild.getBoundingClientRect();
      }
    }

    // Gap between tooltip and anchor element
    const GAP = 8;

    let top = 0;
    let left = 0;
    let transform = '';

    // Treat "none" the same as "top" positioning-wise (but arrow will be hidden)
    const effectivePlacement = placement === 'none' ? 'top' : placement;

    switch (effectivePlacement) {
      case 'bottom':
        top = rect.bottom + GAP + window.scrollY;
        left = rect.left + rect.width / 2 + window.scrollX;
        transform = 'translateX(-50%)';
        break;
      case 'left':
        top = rect.top + rect.height / 2 + window.scrollY;
        left = rect.left - GAP + window.scrollX;
        transform = 'translate(-100%, -50%)';
        break;
      case 'right':
        top = rect.top + rect.height / 2 + window.scrollY;
        left = rect.right + GAP + window.scrollX;
        transform = 'translateY(-50%)';
        break;
      case 'top':
      default:
        top = rect.top - GAP + window.scrollY;
        left = rect.left + rect.width / 2 + window.scrollX;
        transform = 'translate(-50%, -100%)';
        break;
    }

    setStyle({
      position: 'absolute',
      left,
      top,
      transform,
      zIndex: 1000,
    });
  }, [anchorRef, placement]);

  useLayoutEffect(() => {
    updatePositionStable();

    // Recalculate on window resize or scroll to keep tooltip in sync.
    window.addEventListener('resize', updatePositionStable);
    window.addEventListener('scroll', updatePositionStable, { passive: true });

    timeoutRef.current = window.setTimeout(() => {
      setAnimate(true);
    }, animationDelay);

    return () => {
      window.removeEventListener('resize', updatePositionStable);
      window.removeEventListener('scroll', updatePositionStable);
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, [anchorRef, placement, animationDelay]);

  if (!style) {
    return null;
  }

  const ARROW_SIZE = 8; // px

  // Only render the arrow when placement is not "none"
  const renderArrow = placement !== 'none';

  const arrowStyle: React.CSSProperties = {
    position: 'absolute',
    width: `${ARROW_SIZE}px`,
    height: `${ARROW_SIZE}px`,
    background: 'white',
    border: '1px solid rgba(0,0,0,0.7)',
    transform: 'rotate(45deg)',
  };

  if (renderArrow) {
    switch (placement) {
      case 'bottom':
        {
          const horizontalBase: Partial<React.CSSProperties> =
            arrowPosition === 'left'
              ? { left: '10%', transform: 'translateX(-50%) rotate(45deg)' }
              : arrowPosition === 'right'
                ? { right: '10%', transform: 'translateX(50%) rotate(45deg)' }
                : { left: '50%', transform: 'translateX(-50%) rotate(45deg)' };
          Object.assign(arrowStyle, {
            top: `-${ARROW_SIZE / 2}px`,
            ...horizontalBase,
            borderBottomColor: 'transparent',
            borderRightColor: 'transparent',
          });
        }
        break;
      case 'left':
        Object.assign(arrowStyle, {
          right: `-${ARROW_SIZE / 2}px`,
          top: '50%',
          transform: 'translateY(-50%) rotate(45deg)',
          borderLeftColor: 'transparent',
          borderBottomColor: 'transparent',
        });
        break;
      case 'right':
        Object.assign(arrowStyle, {
          left: `-${ARROW_SIZE / 2}px`,
          top: '50%',
          transform: 'translateY(-50%) rotate(45deg)',
          borderRightColor: 'transparent',
          borderTopColor: 'transparent',
        });
        break;
      case 'top':
      default:
        {
          const horizontalBase: Partial<React.CSSProperties> =
            arrowPosition === 'left'
              ? { left: '10%', transform: 'translate(-50%, 0) rotate(45deg)' }
              : arrowPosition === 'right'
                ? { right: '10%', transform: 'translate(50%, 0) rotate(45deg)' }
                : { left: '50%', transform: 'translateX(-50%) rotate(45deg)' };
          Object.assign(arrowStyle, {
            bottom: `-${ARROW_SIZE / 2}px`,
            ...horizontalBase,
            borderTopColor: 'transparent',
            borderLeftColor: 'transparent',
          });
        }
        break;
    }
  }

  // Determine order of elements for right placement (close button on left)
  const contentFlexDirection = placement === 'right' ? 'row-reverse' : 'row';

  return (
    <Portal>
      <Box style={style}>
        <Box
          position="relative"
          display="inline-block"
          animation={animate ? `${wiggle} 1s ease-in-out infinite` : undefined}
        >
          {/* Arrow */}
          {renderArrow && <Box style={arrowStyle} bg="white" />}

          {/* Tooltip content */}
          <Box
            bg="white"
            px={2}
            py={1}
            borderRadius="4px"
            boxShadow="md"
            display="flex"
            alignItems="center"
            border="1px solid rgba(0,0,0,0.7)"
            flexDirection={contentFlexDirection as 'row' | 'row-reverse'}
          >
            {placement === 'right' ? (
              <>
                {showDismiss && (
                  <IconButton
                    size="xs"
                    aria-label="Snooze"
                    variant="ghost"
                    bg="transparent"
                    _hover={{ bg: 'transparent' }}
                    _active={{ bg: 'transparent' }}
                    padding={0}
                    icon={<SnoozeIcon boxSize={4} />}
                    onClick={onDismiss}
                    mr={-1}
                    ml={1}
                    mb={1}
                    mt={-1}
                  />
                )}
                <Box fontSize="sm" whiteSpace="nowrap">
                  <i>{message}</i>
                </Box>
              </>
            ) : (
              <>
                <Box fontSize="sm" whiteSpace="nowrap">
                  <i>{message}</i>
                </Box>
                {showDismiss && (
                  <IconButton
                    size="xs"
                    aria-label="Snooze"
                    variant="ghost"
                    bg="transparent"
                    _hover={{ bg: 'transparent' }}
                    _active={{ bg: 'transparent' }}
                    padding={0}
                    icon={<SnoozeIcon boxSize={4} />}
                    onClick={onDismiss}
                    mr={-1}
                    ml={2}
                    mb={1}
                    mt={-1}
                  />
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Portal>
  );
}
